import React, { useMemo, useRef } from "react";

function lineRange(value, start, end) {
  const lineStart = value.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
  const lineEndCandidate = value.indexOf("\n", end);
  const lineEnd = lineEndCandidate === -1 ? value.length : lineEndCandidate;
  return { lineStart, lineEnd };
}

function replaceRange(value, start, end, insertText) {
  const next = `${value.slice(0, start)}${insertText}${value.slice(end)}`;
  return { next, selectionStart: start, selectionEnd: start + insertText.length };
}

function toggleLinePrefix(value, start, end, prefix) {
  const { lineStart, lineEnd } = lineRange(value, start, end);
  const segment = value.slice(lineStart, lineEnd);
  const lines = segment.split("\n");
  const nonEmpty = lines.filter((line) => line.trim() !== "");
  const hasAll = nonEmpty.length > 0 && nonEmpty.every((line) => line.startsWith(prefix));

  const nextLines = lines.map((line) => {
    if (!line.trim()) return line;
    if (hasAll) return line.startsWith(prefix) ? line.slice(prefix.length) : line;
    return `${prefix}${line}`;
  });

  return replaceRange(value, lineStart, lineEnd, nextLines.join("\n"));
}

function toggleNumberedList(value, start, end) {
  const { lineStart, lineEnd } = lineRange(value, start, end);
  const segment = value.slice(lineStart, lineEnd);
  const lines = segment.split("\n");
  const nonEmpty = lines.filter((line) => line.trim() !== "");
  const numberedRegex = /^\d+\.\s+/;
  const hasAll = nonEmpty.length > 0 && nonEmpty.every((line) => numberedRegex.test(line));

  const nextLines = lines.map((line, index) => {
    if (!line.trim()) return line;
    if (hasAll) return line.replace(numberedRegex, "");
    const raw = line.replace(numberedRegex, "");
    return `${index + 1}. ${raw}`;
  });

  return replaceRange(value, lineStart, lineEnd, nextLines.join("\n"));
}

function toggleHeading(value, start, end, level) {
  const headingPrefix = `${"#".repeat(Math.max(1, Math.min(6, level)))} `;
  const headingRegex = /^#{1,6}\s+/;
  const { lineStart, lineEnd } = lineRange(value, start, end);
  const segment = value.slice(lineStart, lineEnd);
  const lines = segment.split("\n");
  const nonEmpty = lines.filter((line) => line.trim() !== "");
  const hasAll = nonEmpty.length > 0 && nonEmpty.every((line) => line.startsWith(headingPrefix));

  const nextLines = lines.map((line) => {
    if (!line.trim()) return line;
    const withoutHeading = line.replace(headingRegex, "");
    return hasAll ? withoutHeading : `${headingPrefix}${withoutHeading}`;
  });

  return replaceRange(value, lineStart, lineEnd, nextLines.join("\n"));
}

function wrapInline(value, start, end, before, after = before) {
  const selected = value.slice(start, end);
  const hasWrapper = selected.startsWith(before) && selected.endsWith(after) && selected.length >= before.length + after.length;
  if (hasWrapper) {
    const unwrapped = selected.slice(before.length, selected.length - after.length);
    return replaceRange(value, start, end, unwrapped);
  }

  if (!selected) {
    const insert = `${before}${after}`;
    return {
      next: `${value.slice(0, start)}${insert}${value.slice(end)}`,
      selectionStart: start + before.length,
      selectionEnd: start + before.length
    };
  }

  return replaceRange(value, start, end, `${before}${selected}${after}`);
}

export default function SimpleTextEditor({
  value,
  onChange,
  placeholder = "",
  rows = 6,
  ariaLabel = "text-editor",
  className = "",
  minHeight = "",
  ...rest
}) {
  const editorRef = useRef(null);
  const editorStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "block",
    whiteSpace: "pre-wrap",
    wordBreak: "normal",
    overflowWrap: "break-word",
    writingMode: "horizontal-tb",
    textOrientation: "mixed",
    direction: "ltr",
    unicodeBidi: "plaintext",
    ...(minHeight ? { minHeight } : {})
  };

  const applyChange = ({ next, selectionStart, selectionEnd }) => {
    onChange(next);
    requestAnimationFrame(() => {
      if (!editorRef.current) return;
      editorRef.current.focus();
      editorRef.current.setSelectionRange(selectionStart, selectionEnd);
    });
  };

  const characterCount = useMemo(() => String(value || "").length, [value]);

  const applyFromSelection = (transformer) => {
    if (!editorRef.current) return;
    const textarea = editorRef.current;
    const current = textarea.value || "";
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? start;
    applyChange(transformer(current, start, end));
  };

  return (
    <div className={`simple-text-editor ${className}`.trim()}>
      <div className="simple-text-editor-toolbar">
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => toggleHeading(current, start, end, 1))}
        >
          H1
        </button>
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => toggleHeading(current, start, end, 2))}
        >
          H2
        </button>
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => toggleHeading(current, start, end, 3))}
        >
          H3
        </button>
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => wrapInline(current, start, end, "**"))}
        >
          Bold
        </button>
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => wrapInline(current, start, end, "_"))}
        >
          Italic
        </button>
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => toggleLinePrefix(current, start, end, "- "))}
        >
          Bullet
        </button>
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => toggleNumberedList(current, start, end))}
        >
          Number
        </button>
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => toggleLinePrefix(current, start, end, "> "))}
        >
          Quote
        </button>
        <button
          type="button"
          className="editor-tool-btn"
          onClick={() => applyFromSelection((current, start, end) => wrapInline(current, start, end, "[", "](https://)"))}
        >
          Link
        </button>
      </div>
      <textarea
        ref={editorRef}
        rows={rows}
        wrap="soft"
        dir="ltr"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (!(event.ctrlKey || event.metaKey)) return;
          const key = event.key.toLowerCase();
          if (key === "b") {
            event.preventDefault();
            event.stopPropagation();
            applyFromSelection((current, start, end) => wrapInline(current, start, end, "**"));
          }
          if (key === "i") {
            event.preventDefault();
            event.stopPropagation();
            applyFromSelection((current, start, end) => wrapInline(current, start, end, "_"));
          }
          if (key === "k") {
            event.preventDefault();
            event.stopPropagation();
            applyFromSelection((current, start, end) => wrapInline(current, start, end, "[", "](https://)"));
          }
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        style={editorStyle}
        {...rest}
      />
      <div className="simple-text-editor-meta">
        <small>{characterCount} characters</small>
      </div>
    </div>
  );
}
